/**
 * 微信认证服务
 * 基于Session的微信登录认证系统
 */

export interface WechatLoginRequest {
    weChatOpenId: string;
}

export interface UserInfo {
    userId?: string;
    email?: string;
    login?: string;
    name?: string;
    phone?: string;
    weChatOpenId?: string;
    supplierCode?: string;
    supplierCodeShow?: string;
    supplierName?: string;
    loginTime?: string;
    lastAccessTime?: string;
}

export interface WechatLoginResponse {
    success: boolean;
    message: string;
    userInfo: UserInfo | null;
    sessionId: string | null;
}

export interface CheckLoginResponse {
    success: boolean;
    loggedIn: boolean;
    message: string;
    userInfo: UserInfo | null;
}

export interface CurrentUserResponse {
    success: boolean;
    message: string;
    loggedIn: boolean;
    userInfo: UserInfo | null;
}

export interface LogoutResponse {
    success: boolean;
    message: string;
}

// 使用相对路径，通过代理访问后端
const BASE_URL = '/api';

/**
 * 微信登录
 */
export const wechatLogin = async (weChatOpenId: string): Promise<WechatLoginResponse> => {
    try {
        console.log('发送微信登录请求:', { weChatOpenId });

        const response = await fetch(`${BASE_URL}/auth/wechat-login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include', // 重要：包含cookie
            body: JSON.stringify({
                weChatOpenId: weChatOpenId
            }),
        });

        const result = await response.json();
        console.log('微信登录响应:', result);

        return result;
    } catch (error) {
        console.error('微信登录请求错误:', error);
        return {
            success: false,
            message: error instanceof Error ? error.message : '网络错误，请检查网络连接后重试',
            userInfo: null,
            sessionId: null,
        };
    }
};

/**
 * 检查登录状态
 * @param wechatOpenId - 微信OpenID（可选，如果不传则从URL获取）
 */
export const checkLogin = async (wechatOpenId?: string): Promise<CheckLoginResponse> => {
    try {
        // 如果没有传入wechatOpenId，尝试从URL获取
        const openId = wechatOpenId || getWeChatOpenIdFromUrl();

        if (!openId) {
            console.warn('未提供wechatOpenId参数');
            return {
                success: false,
                loggedIn: false,
                message: '未提供微信OpenID',
                userInfo: null,
            };
        }

        const response = await fetch(`${BASE_URL}/auth/check-login?wechatOpenId=${encodeURIComponent(openId)}`, {
            credentials: 'include', // 重要：包含cookie
        });

        const result = await response.json();
        console.log('检查登录状态响应:', result);

        // 适配后端响应格式：hasProfile 表示已登录
        return {
            success: result.success,
            loggedIn: result.hasProfile || false, // 使用 hasProfile 判断是否已登录
            message: result.message,
            userInfo: result.userProfile || null, // 使用 userProfile 作为用户信息
        };
    } catch (error) {
        console.error('检查登录状态错误:', error);
        return {
            success: false,
            loggedIn: false,
            message: error instanceof Error ? error.message : '网络错误',
            userInfo: null,
        };
    }
};

/**
 * 获取当前用户信息
 */
export const getCurrentUser = async (): Promise<CurrentUserResponse> => {
    try {
        const response = await fetch(`${BASE_URL}/auth/current-user`, {
            credentials: 'include', // 重要：包含cookie
        });

        const result = await response.json();
        console.log('获取当前用户信息响应:', result);

        return result;
    } catch (error) {
        console.error('获取当前用户信息错误:', error);
        return {
            success: false,
            message: error instanceof Error ? error.message : '网络错误',
            loggedIn: false,
            userInfo: null,
        };
    }
};

/**
 * 用户登出
 */
export const logout = async (): Promise<LogoutResponse> => {
    try {
        const response = await fetch(`${BASE_URL}/auth/logout`, {
            method: 'POST',
            credentials: 'include', // 重要：包含cookie
        });

        const result = await response.json();
        console.log('登出响应:', result);

        return result;
    } catch (error) {
        console.error('登出错误:', error);
        return {
            success: false,
            message: error instanceof Error ? error.message : '网络错误',
        };
    }
};

/**
 * 从URL获取微信OpenId
 */
export const getWeChatOpenIdFromUrl = (): string | null => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('weChatOpenId');
};

/**
 * 检查是否有微信OpenId
 */
export const hasWeChatOpenId = (): boolean => {
    const openId = getWeChatOpenIdFromUrl();
    return !!openId && openId.trim().length > 0;
};
